import { useMemo } from 'react';
import { calculateEloRatings, EloRating } from '../utils/eloCalculations';
import { calculateStandings, TeamStandings } from '../utils/standingsCalculations';
import { Division as DivisionModel, Week as WeekModel } from '../model/Season';
import { Coach as CoachModel } from '../model/Coach';

interface SeasonCalculationsProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
    };
    leagueCoaches: CoachModel[];
}

interface SeasonCalculations {
    eloRatings: EloRating[];
    allStandings: TeamStandings[];
    divisionStandings: Record<string, TeamStandings[]>;
}

export const useSeasonCalculations = ({
    seasonData,
    leagueCoaches
}: SeasonCalculationsProps): SeasonCalculations => {
    // Memoize ELO ratings calculation
    const eloRatings = useMemo(() => {
        return calculateEloRatings(
            seasonData.divisions,
            seasonData.weeks,
            leagueCoaches
        );
    }, [seasonData.divisions, seasonData.weeks, leagueCoaches]);

    // Memoize all standings calculation
    const allStandings = useMemo(() => {
        return calculateStandings(
            seasonData.divisions,
            seasonData.weeks,
            leagueCoaches
        );
    }, [seasonData.divisions, seasonData.weeks, leagueCoaches]);

    // Memoize division standings calculation
    const divisionStandings = useMemo(() => {
        const standings: Record<string, TeamStandings[]> = {};
        seasonData.divisions.forEach(division => {
            standings[division.divisionName] = allStandings.filter(
                team => team.divisionName === division.divisionName
            );
        });
        return standings;
    }, [allStandings, seasonData.divisions]);

    return {
        eloRatings,
        allStandings,
        divisionStandings
    };
}; 